<template>
  <li class="tournament-bracket__item">
    <div class="tournament-bracket__match" :class="{ 'is-current': isCurrent }" tabindex="0">
      <div :class="['bracket-match__content', matchClass]">
        <div class="bm-header">
          <span class="bm-vs">{{ vsTitle }}</span>
          <span ref="mapSpan" class="bm-map" @mouseenter="mapHoverStart" @mouseleave="mapHoverEnd">
            <a :href="mapLink" class="bm-map-link" title="See this map's stats">🗺 {{ map }}</a>
          </span>
          <button v-if="onFight && activeLeft && activeRight"
                  class="fight-btn"
                  @click.stop="onFight(activeLeft.name, activeRight.name, map, matchIndex)">⚔ Fight</button>
          <button v-if="activeLeft && activeRight"
                  class="odds-btn" :disabled="oddsLoading"
                  @click.stop="computeOdds"
                  title="Run the battle sim for this matchup">{{ oddsLoading ? '…' : (oddsShown ? '↻ odds' : '% odds') }}</button>
          <button v-if="challengers.length"
                  class="odds-btn def-btn" :disabled="defenseLoading"
                  @click.stop="computeDefenseOdds"
                  title="Sim the champion vs every team still alive in the bracket">{{ defenseLoading ? '…' : (defenseOdds ? '↻ threats' : '🛡 threats') }}</button>
        </div>

        <div v-if="predict" class="bm-predict" :class="predict.winner ? 'graded' : 'open'">
          <template v-if="!predict.winner">
            <span class="bmp-q">🔮 Pick:</span>
            <template v-for="(t, idx) in predict.candidates" :key="idx">
              <button v-if="t" class="bmp-btn" :class="[t + '-team', { picked: predict.picked === t }]"
                      @click.stop="$emit('pick', t)">
                <span v-if="predict.picked === t" class="bmp-dot">🔮</span>{{ teamLabel(t) }}
              </button>
            </template>
            <span v-if="!predict.candidates[0] || !predict.candidates[1]" class="bmp-wait">— pick the earlier round first</span>
          </template>
          <template v-else>
            <span class="bmp-res" :class="!predict.picked ? 'none' : (predict.picked === predict.winner ? 'hit' : 'miss')">
              <template v-if="!predict.picked">— no pick</template>
              <template v-else-if="predict.picked === predict.winner">✓ {{ teamLabel(predict.picked) }} +{{ predict.weight }}</template>
              <template v-else>✗ you said {{ teamLabel(predict.picked) }}</template>
            </span>
            <span v-if="predict.simPick" class="bmp-sim" :class="predict.simPick === predict.winner ? 'hit' : 'miss'"
                  title="The battle sim's pick">🤖 {{ teamLabel(predict.simPick) }} {{ predict.simPick === predict.winner ? '✓' : '✗' }}</span>
          </template>
        </div>

        <template v-for="team in leftTeams" :key="'L-' + team.name">
          <div v-if="team.show"
               @mousedown="onBracketClick"
               @mouseup="onGoto(team.name)"
               :class="['left-team', team.name + '-team', { won: leftWon, lost: rightWon }]">
            <div class="team-chip">
              <span v-if="leftWon" class="win-mark" title="Won">✓ </span>{{ teamByName[team.name].label }}<span v-if="teamGrades[team.name]" class="grade-badge hasToolTip" :class="'tier-' + teamGrades[team.name].color">{{ teamGrades[team.name].grade }}<span class="toolTipSide tr-host" @click.stop><TeamRatingTip :grade="teamGrades[team.name]" :label="teamByName[team.name].label" /></span></span>
              <span v-if="teamByName[team.name].label === 'Champion'" class="crown">👑</span>
              <ChampStreakBadge v-if="teamByName[team.name].label === 'Champion'" :streak="champStreak" />
              <span v-if="teamOdds(team.name) != null" class="chip-odds" :class="{ fav: teamOdds(team.name) >= 50 }" title="Estimated win chance (battle sim)">{{ teamOdds(team.name) }}%</span>
            </div>
            <div class="unit-grid">
              <div class="unit-cell hasToolTip"
                   v-for="(member, idx) in teamByName[team.name].roster" :key="idx"
                   :class="{ 'is-boss': member.RaidBoss }">
                <UnitSprite v-if="useSprites"
                            :unit="member"
                            :team-color="team.name === activeLeft.name ? cellColors.left : cellColors.right"
                            :fallback-src="portrait(member, teamByName[team.name].label)"
                            :animated="true"
                            :attack-bursts="attackAnims"
                            :flip="team.name === activeLeft.name"
                            :size="52" />
                <img v-else class="unit-icon" :src="portrait(member, teamByName[team.name].label)"
                     :alt="member.Class" @error="$event.target.style.visibility='hidden'">
                <span class="unit-name" :style="nameStyle(member.Name)">
                  <span v-if="member.RaidBoss" class="boss-badge">★</span>{{ member.Name }}
                </span>
                <div class="toolTipSide"
                     :style="{ background: teamDark(teamByName[team.name].label), borderColor: teamBright(teamByName[team.name].label) }">
                  <HoverUnit :allunits="items"
                             :enemyColor="team.enemyColor"
                             :teamColor="teamByName[team.name].label"
                             :notAbles="notAbles" :charStats="charStats"
                             :members="member"
                             :itemschanged="itemschanged"
                             :abilitiesInfo="abilitiesInfo" :toolTips="toolTips"
                             :highlightColors="highlightColors"
                             :show-win-rates="showWinRates" />
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-for="team in rightTeams" :key="'R-' + team.name">
          <div v-if="team.show"
               @mousedown="onBracketClick"
               @mouseup="onGoto(team.name)"
               :class="['right-team', team.name + '-team', { won: rightWon, lost: leftWon }]">
            <div class="team-chip">
              <span v-if="rightWon" class="win-mark" title="Won">✓ </span>{{ teamByName[team.name].label }}<span v-if="teamGrades[team.name]" class="grade-badge hasToolTip" :class="'tier-' + teamGrades[team.name].color">{{ teamGrades[team.name].grade }}<span class="toolTipSide tr-host" @click.stop><TeamRatingTip :grade="teamGrades[team.name]" :label="teamByName[team.name].label" /></span></span>
              <span v-if="teamByName[team.name].label === 'Champion'" class="crown">👑</span>
              <ChampStreakBadge v-if="teamByName[team.name].label === 'Champion'" :streak="champStreak" />
              <span v-if="teamOdds(team.name) != null" class="chip-odds" :class="{ fav: teamOdds(team.name) >= 50 }" title="Estimated win chance (battle sim)">{{ teamOdds(team.name) }}%</span>
            </div>
            <div class="unit-grid">
              <div class="unit-cell hasToolTip"
                   v-for="(member, idx) in teamByName[team.name].roster" :key="idx"
                   :class="{ 'is-boss': member.RaidBoss }">
                <UnitSprite v-if="useSprites"
                            :unit="member"
                            :team-color="team.name === activeLeft.name ? cellColors.left : cellColors.right"
                            :fallback-src="portrait(member, teamByName[team.name].label)"
                            :animated="true"
                            :attack-bursts="attackAnims"
                            :flip="team.name === activeLeft.name"
                            :size="52" />
                <img v-else class="unit-icon" :src="portrait(member, teamByName[team.name].label)"
                     :alt="member.Class" @error="$event.target.style.visibility='hidden'">
                <span class="unit-name" :style="nameStyle(member.Name)">
                  <span v-if="member.RaidBoss" class="boss-badge">★</span>{{ member.Name }}
                </span>
                <div class="toolTipSide"
                     :style="{ background: teamDark(teamByName[team.name].label), borderColor: teamBright(teamByName[team.name].label) }">
                  <HoverUnit :allunits="items"
                             :enemyColor="team.enemyColor"
                             :teamColor="teamByName[team.name].label"
                             :notAbles="notAbles" :charStats="charStats"
                             :members="member"
                             :itemschanged="itemschanged"
                             :abilitiesInfo="abilitiesInfo" :toolTips="toolTips"
                             :highlightColors="highlightColors"
                             :show-win-rates="showWinRates" />
                </div>
              </div>
            </div>
          </div>
        </template>

        <div v-if="defenseOdds" class="def-box">
          <div class="def-box-title">Chance to beat champ</div>
          <div class="def-rows">
            <span v-for="row in defenseOdds" :key="row.name" class="def-row" :style="rowStyle(row.name)">
              <span class="def-name">{{ row.label }}</span><span class="def-pct">{{ row.pct }}%</span>
            </span>
          </div>
          <div v-if="defenseLoading" class="def-running">running… {{ defenseOdds.length }}/{{ challengers.length }}</div>
        </div>
      </div>
    </div>

    <!-- 3D map hover preview. Teleported to <body> so it can never get caught
         under sibling bracket rounds (the old in-DOM tooltip's stacking bug);
         pointer-events:none — pure display, the map-title link does navigation. -->
    <Teleport to="body">
      <div v-if="mapPopOpen" class="map3d-pop" :style="mapPopStyle">
        <h3 class="map3d-title">{{ map }}</h3>
        <MapViewer3D v-if="popTerrain" :key="map" :terrain="popTerrain" :starts="popStarts"
                     :mesh-url="popMeshUrl" :auto-rotate="true" :chrome="false" style="height: 300px;" />
        <div v-else class="map-holder">
          <img v-for="n in 4" :key="n" :src="imgUrlMaps + (map || '').split(')')[0] + '_' + n + '.gif'" />
        </div>
        <span class="map3d-hint">click the map name for full stats</span>
      </div>
    </Teleport>
  </li>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import HoverUnit from './hoverUnit.vue';
import UnitSprite from './UnitSprite.vue';
import mapTerrain from '../data/mapTerrain.json';
import mapTiles from '../data/mapTiles.json';
import { tilesForMap } from '../lib/fftPath.js';
import { MESH_URLS } from '../lib/meshUrls.js';
import { startsWithSprites, matchColors } from '../lib/spriteForClass.js';

// Lazy — three.js (~600KB) + the viewer only load on the first map hover.
const MapViewer3D = defineAsyncComponent(() => import('./MapViewer3D.vue'));
import { teamBright as brightColor, teamDark as darkColor } from '../lib/teamColors.js';
import { prepTeam } from '../lib/simPrep.js';
import { runBattles } from '../lib/battleSim.js';
import { calibratedOdds } from '../lib/simCalibrationLive.js';
import battleConfig from '../data/battleConfig.json';
import monsterSkills from '../data/monsterSkills.json';
import { teamGrade } from '../lib/unitGrade.js';
import { rosterStats } from '../lib/preload.js';

import { UNITS_BASE as UNIT_IMG } from '../lib/assetUrls.js';
import ChampStreakBadge from './ChampStreakBadge.vue';
import TeamRatingTip from './TeamRatingTip.vue';
// Solid team colors (match the .team-chip backgrounds below) for the champion
// "threat board" rows. Keyed by the lowercase team name.
const CHIP_COLORS = { red: '#c0392b', blue: '#2647c8', green: '#2e9e2e', yellow: '#b9960f', white: '#d9d9d9', black: '#222', purple: '#6a1b9a', brown: '#6d4326', champion: '#e07b1a' };

export default {
  components: { HoverUnit, ChampStreakBadge, MapViewer3D, UnitSprite, TeamRatingTip },

  data() {
    return { odds: null, oddsLoading: false, defenseOdds: null, defenseLoading: false, mapPopOpen: false, mapPopPos: { x: 0, y: 0 } };
  },
  props: {
    // The map title for this round, from `maps[i]`.
    map: { type: String, default: '' },
    // Champ cell only: the reigning champion's title-defense streak (streak.txt),
    // shown as an escalating 🔥 badge next to the champion's crown.
    champStreak: { type: Number, default: 0 },
    // Display string for the matchup heading. For QF cells this is hardcoded
    // ("Red vs. Blue"); for later rounds it's derived from the semi/final/
    // champ-label computeds in Luxstadio.
    vsTitle: { type: String, default: '' },
    // Optional class added to .bracket-match__content (e.g. "semi", "final",
    // "champ") so the existing bracket CSS targets the cell appropriately.
    matchClass: { type: String, default: '' },
    // [{ name, show, enemyColor }] — `show` decides whether that team's
    // roster is rendered (used by SF/Finals/Champ to gate by advancement),
    // `enemyColor` is the label passed to HoverUnit for the unit-card's
    // opposing-team color.
    leftTeams: { type: Array, required: true },
    rightTeams: { type: Array, required: true },
    // Whether the left/right side won this cell — drives the green
    // "won" / red "lost" classes on each team div.
    leftWon: { type: Boolean, default: false },
    rightWon: { type: Boolean, default: false },
    // Shared lookups + URL prefix passed through from Luxstadio.
    teamByName: { type: Object, required: true },
    items: { type: Object, required: true },
    notAbles: { type: Object, required: true },
    charStats: { type: Object, required: true },
    itemschanged: { type: Object, required: true },
    abilitiesInfo: { type: Object, default: () => ({}) },
    toolTips: { type: Object, default: () => ({}) },
    imgUrlMaps: { type: String, required: true },
    // Map<lowercase-username, color>. Forwarded to HoverUnit and also used
    // here to tint the bracket-cell roster lines.
    highlightColors: { type: Object, default: () => new Map() },
    // Click handlers — function props instead of emits because the parent
    // already has the methods (bracketClick/goto) and there's no useful
    // payload transformation in between.
    onBracketClick: { type: Function, required: true },
    onGoto: { type: Function, required: true },
    // Opens the fight view for this cell's two combatants. Optional so the
    // component still works if a caller doesn't wire it.
    onFight: { type: Function, default: null },
    // True for the next undecided match — glows the cell.
    isCurrent: { type: Boolean, default: false },
    // This cell's match index (0-7), forwarded to the Fight View so it can carry
    // the predict context for the matchup.
    matchIndex: { type: Number, default: null },
    // Champ cell only: the still-alive challenger team names. When non-empty,
    // the cell offers a "threat board" — the champion's odds vs each of them.
    challengers: { type: Array, default: () => [] },
    showWinRates: { type: Boolean, default: false },
    // When true (default), the live-match sprites occasionally play their attack
    // animation. Threaded from prefs.attackAnims via Luxstadio.bracketSharedProps.
    attackAnims: { type: Boolean, default: true },
    // Predict-the-Bracket state for this cell, or null when predict mode is off.
    // { candidates:[teamA,teamB], picked, simPick, winner, weight, roundLabel }
    predict: { type: Object, default: null },
  },
  emits: ['pick'],

  computed: {
    // Team grade (mean unit grade) for each team shown in this cell.
    teamGrades() {
      const gd = { charStats: this.charStats, itemschanged: this.itemschanged, notAbles: this.notAbles };
      const out = {};
      for (const t of [...this.leftTeams, ...this.rightTeams]) { const tb = this.teamByName[t.name]; if (tb) out[t.name] = teamGrade(tb.roster, rosterStats, gd); }
      return out;
    },
    // The teams actually shown in this cell (the resolved combatants).
    activeLeft() { return this.leftTeams.find((t) => t.show); },
    activeRight() { return this.rightTeams.find((t) => t.show); },
    // Animate the roster portraits as walking sprites ONLY in the current
    // (glowing) match — 8 canvases, the cell the eye is already on. Every other
    // cell keeps the cheap static headshots.
    useSprites() { return this.isCurrent && !!this.activeLeft && !!this.activeRight; },
    // The two combatants' resolved sprite colors (matchColors handles the
    // champion taking its alt color on a clash). Left team faces right.
    cellColors() {
      const L = (this.activeLeft && this.teamByName[this.activeLeft.name]) || null;
      const R = (this.activeRight && this.teamByName[this.activeRight.name]) || null;
      const [left, right] = matchColors(L, R);
      return { left, right };
    },
    // Show cached odds only while they still match the current matchup, so a
    // new tournament / re-resolved cell drops stale numbers automatically.
    oddsShown() {
      return this.odds && this.activeLeft && this.activeRight
        && this.odds.left === this.activeLeft.name && this.odds.right === this.activeRight.name;
    },
    // Identity of the current challenger set — changes when a round resolves, so
    // a stale threat board (listing a now-eliminated team) gets dropped.
    challengerKey() { return this.challengers.join(','); },
    // Deep-link to this map's full page on the Maps view. The map prop is
    // "NN) Name"; strip the number prefix and slugify the name to match
    // MapsView's ?maps&map=<slug> deep-link (slugify is duplicated there).
    mapLink() {
      const name = (this.map || '').replace(/^\s*\d+\)\s*/, '').trim();
      if (!name) return null;
      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      return '?maps&map=' + slug;
    },
    // Terrain for the hover popup — same lookup as FightModal: byName override
    // (reused map numbers) first, then the number key. null ⇒ gif fallback.
    popTiles() { return tilesForMap(this.map, mapTiles); }, // the game's tile table: the sim walks by FFT's pathfinder
    popTerrain() {
      const m = (this.map || '').match(/^\s*(\d+)\)\s*(.*)$/);
      const num = m ? +m[1] : null;
      const bare = (m ? m[2] : this.map || '').trim();
      return (mapTerrain.byName && mapTerrain.byName[bare]) || (num != null && mapTerrain.maps ? mapTerrain.maps[num] : null) || null;
    },
    // The hover popup's start tiles, each tagged with the sprite of the unit that
    // actually stands there: Player 1 = the left combatant, Player 2 = the right
    // (the sim's convention), start.unit = that team's roster slot. Falls back to
    // a bare start (→ Knight) when a side/roster slot isn't resolved.
    popStarts() {
      const t = this.popTerrain;
      if (!t || !t.starts) return [];
      const teamObj = (tt) => (tt && this.teamByName[tt.name]) || null;
      return startsWithSprites(t.starts, teamObj(this.activeLeft), teamObj(this.activeRight));
    },
    popMeshUrl() {
      const m = (this.map || '').match(/^\s*(\d+)\)\s*(.*)$/);
      if (!m) return null;
      // A byName terrain override means this name was displaced off its number
      // (reused map numbers) — the number's GLB would be the WRONG map's mesh,
      // so fall back to the schematic built from the (correct) byName terrain.
      if (mapTerrain.byName && mapTerrain.byName[m[2].trim()]) return null;
      return MESH_URLS[+m[1]] || null;
    },
    mapPopStyle() {
      return { left: this.mapPopPos.x + 'px', top: this.mapPopPos.y + 'px' };
    },
  },

  watch: {
    challengerKey() { this.defenseOdds = null; this.defenseLoading = false; this._defRun = (this._defRun || 0) + 1; },
  },

  beforeUnmount() {
    this.mapHoverEnd();
  },

  methods: {
    // ----- 3D map hover popup -----
    // Hover-intent delay so an incidental mouse pass doesn't pull three.js +
    // a GLB; desktop/hover-capable only (mobile gets the map-title link).
    mapHoverStart() {
      if (!this.map) return;
      if (!window.matchMedia('(min-width: 1200px) and (hover: hover)').matches) return;
      this._mapHoverT = setTimeout(() => {
        const span = this.$refs.mapSpan;
        if (!span) return;
        const r = span.getBoundingClientRect();
        const W = 420, H = 366, PAD = 8;
        let x = r.right + 14;
        if (x + W > window.innerWidth - PAD) x = r.left - W - 14;   // flip left near the right edge
        x = Math.max(PAD, x);
        const y = Math.max(PAD, Math.min(r.top - 10, window.innerHeight - H - PAD));
        this.mapPopPos = { x, y };
        this.mapPopOpen = true;
        // The popup is position:fixed — close it if the page scrolls away.
        window.addEventListener('scroll', this._mapScrollClose = () => this.mapHoverEnd(), { once: true, passive: true });
      }, 250);
    },
    mapHoverEnd() {
      clearTimeout(this._mapHoverT);
      if (this._mapScrollClose) { window.removeEventListener('scroll', this._mapScrollClose); this._mapScrollClose = null; }
      this.mapPopOpen = false;
    },
    nameStyle(name) {
      const c = this.highlightColors && this.highlightColors.get((name || '').toLowerCase());
      return c ? { color: c, fontWeight: 'bold' } : null;
    },
    teamBright(label) { return brightColor(label); },
    teamDark(label) { return darkColor(label); },
    // Display label for a lowercase team name (for the predict bar).
    teamLabel(name) { return this.teamByName[name] ? this.teamByName[name].label : name; },
    // Per-team win% for this cell's chip, once odds are computed for the current
    // matchup (else null → the chip shows no number). Maps the team to a/b by name.
    teamOdds(name) {
      if (!this.oddsShown) return null;
      if (name === this.odds.left) return this.odds.a;
      if (name === this.odds.right) return this.odds.b;
      return null;
    },
    chipColor(name) { return CHIP_COLORS[name] || '#555'; },
    rowStyle(name) {
      return name === 'white'
        ? { background: CHIP_COLORS.white, color: '#222', textShadow: 'none' }
        : { background: this.chipColor(name), color: '#fff' };
    },
    // Champion "threat board": sim the champion vs each still-alive challenger,
    // revealing each challenger's chance to WIN as it finishes (sorted, so the
    // biggest threat floats to the top). Chained via setTimeout so the UI stays
    // responsive and rows pop in instead of freezing for ~2s.
    computeDefenseOdds() {
      if (this.defenseLoading || !this.challengers.length) return;
      const champTb = this.teamByName.champion;
      if (!champTb) return;
      this.defenseLoading = true;
      this.defenseOdds = [];
      const myRun = (this._defRun = (this._defRun || 0) + 1);
      const data = { charStats: this.charStats, itemschanged: this.itemschanged, notAbles: this.notAbles, abilitiesInfo: this.abilitiesInfo, monsterSkills };
      let champSpec;
      try { champSpec = prepTeam(champTb.roster, data); } catch (e) { this.defenseLoading = false; return; }
      const queue = this.challengers.slice();
      const runNext = () => {
        if (myRun !== this._defRun) return;                 // superseded by a reset / re-run
        const name = queue.shift();
        if (!name) { this.defenseLoading = false; return; }
        setTimeout(() => {
          if (myRun !== this._defRun) return;
          try {
            const tb = this.teamByName[name];
            // Challenger = A, Champion = B → champion gets the incumbency edge (champSide:'B').
            const r = runBattles(prepTeam(tb.roster, data), champSpec, { ...battleConfig, terrain: this.popTerrain, tiles: this.popTiles });
            this.defenseOdds.push({ name, label: tb.label, pct: calibratedOdds(r, { champSide: 'B' }).a });
            this.defenseOdds.sort((a, b) => b.pct - a.pct);
          } catch (e) { /* skip rosters the prep can't classify */ }
          runNext();
        }, 0);
      };
      runNext();
    },
    // Unit sprite URL — same scheme as HoverUnit: team-colored for normal
    // units, plain for the champion team and monsters.
    portrait(member, label) {
      if (member.Gender === 'Monster') return UNIT_IMG + member.Class + '.gif';
      const g = (member.Gender || 'Male')[0];
      if (label === 'Champion') return UNIT_IMG + member.Class + g + '.gif';
      return UNIT_IMG + member.Class + g + '_' + label + '.gif';
    },
    // On-demand head-to-head win% for this cell's two combatants. Runs the
    // Monte-Carlo sim (blocking ~0.3s), deferred a tick so the button shows a
    // pending state first. Self-contained; reuses the same prep as FightModal.
    computeOdds() {
      if (!this.activeLeft || !this.activeRight || this.oddsLoading) return;
      const left = this.activeLeft.name, right = this.activeRight.name;
      this.oddsLoading = true;
      setTimeout(() => {
        try {
          const data = { charStats: this.charStats, itemschanged: this.itemschanged, notAbles: this.notAbles, abilitiesInfo: this.abilitiesInfo, monsterSkills };
          const A = prepTeam(this.teamByName[left].roster, data);
          const B = prepTeam(this.teamByName[right].roster, data);
          const r = runBattles(A, B, { ...battleConfig, terrain: this.popTerrain, tiles: this.popTiles });
          const champSide = this.teamByName[left].label === 'Champion' ? 'A' : this.teamByName[right].label === 'Champion' ? 'B' : null;
          const o = calibratedOdds(r, { champSide });
          this.odds = { a: o.a, b: o.b, left, right };
        } catch (e) {
          this.odds = null;
        } finally {
          this.oddsLoading = false;
        }
      }, 0);
    },
  },
};
</script>

<style scoped>
/* Team-rating hover: tighten the generic side box around the compact card
   (TeamRatingTip brings its own layout, so the old 400px/20px box is too big). */
.tr-host { width: auto; max-width: 340px; padding: 11px 13px; line-height: 1.35; }

/* Predict-the-Bracket pick bar */
.bm-predict {
  display: flex; flex-wrap: wrap; align-items: center; justify-content: center;
  gap: 4px 6px; width: 100%; margin: 1px 0 6px; font-size: 12px;
  padding: 3px 6px; border-radius: 6px;
  background: rgba(124, 77, 255, 0.16); border: 1px solid rgba(159, 122, 234, 0.5);
}
.bm-predict.graded { background: rgba(0, 0, 0, 0.28); border-color: rgba(255, 255, 255, 0.18); }
.bmp-q { color: #d6c8ff; font-weight: bold; text-shadow: 1px 1px 2px rgb(0 0 0 / 80%); }
.bmp-btn {
  cursor: pointer; border: 1px solid rgba(255, 255, 255, 0.45); border-radius: 5px;
  padding: 1px 8px; color: #fff; font-weight: bold; font-size: 12px;
  text-shadow: 1px 1px 2px rgb(0 0 0 / 90%); opacity: 0.78; transition: opacity 0.12s, box-shadow 0.12s;
}
.bmp-btn:hover { opacity: 1; }
.bmp-btn.picked { opacity: 1; box-shadow: 0 0 0 2px #fff, 0 0 8px rgba(214, 200, 255, 0.9); }
.bmp-dot { margin-right: 3px; }
.bmp-wait { color: #cbb8ff; font-style: italic; opacity: 0.85; }
.bmp-res { font-weight: bold; text-shadow: 1px 1px 2px rgb(0 0 0 / 85%); }
.bmp-res.hit { color: #57e07a; }
.bmp-res.miss { color: #ff7b7b; }
.bmp-res.none { color: #b9c2cc; }
.bmp-sim { color: #cbe6ff; opacity: 0.92; text-shadow: 1px 1px 2px rgb(0 0 0 / 85%); }
.bmp-sim.hit { color: #8fe0a6; }
.bmp-sim.miss { color: #f3a6a6; }

/* Compact one-line header: matchup · map · fight */
.bm-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 3px 10px;
  width: 100%;
  margin: 2px 0 5px;
  font-size: 12px;
  text-shadow: 1px 1px 2px rgb(0 0 0 / 80%);
}
.bm-vs { font-weight: bold; }
.bm-map { opacity: 0.9; }
.bm-map-link { color: inherit; text-decoration: none; cursor: pointer; }
.bm-map-link:hover { text-decoration: underline; }

/* Teleported 3D map hover preview (display-only; never intercepts the mouse) */
.map3d-pop {
  position: fixed;
  width: 420px;
  z-index: 9999;
  pointer-events: none;
  background: rgba(12, 54, 80, 0.97);
  border: 1px solid #fff;
  border-radius: 6px;
  padding: 8px 10px 6px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.55);
  animation: map3d-in 0.15s ease;
}
@keyframes map3d-in { from { opacity: 0; } to { opacity: 1; } }
.map3d-title { margin: 0 0 6px; font-size: 15px; color: #fff; text-align: center; }
.map3d-pop .map-holder { margin: 0 auto; }
.map3d-hint { display: block; text-align: center; font-size: 11px; color: #fff; opacity: 0.65; margin-top: 4px; }
.fight-btn {
  background: rgba(12, 54, 80, 0.85);
  color: #fff;
  border: 1px solid #fff;
  font-family: inherit;
  font-size: 11px;
  padding: 1px 7px;
  cursor: pointer;
}
.fight-btn:hover { background: #1C6EA4; }
.odds-btn {
  background: rgba(12, 54, 80, 0.85);
  color: #fff;
  border: 1px solid #888;
  font-family: inherit;
  font-size: 11px;
  padding: 1px 7px;
  cursor: pointer;
}
.odds-btn:hover:not(:disabled) { background: #1C6EA4; }
.odds-btn:disabled { opacity: 0.6; cursor: default; }
.def-btn { border-color: #e0b03a; }

/* Champion "threat board" — sits in-flow below the champion inside the champ
   cell (a full-width row of the flex-wrap content), as sorted team-colored pills. */
.def-box {
  width: 100%;
  box-sizing: border-box;
  margin-top: 6px;
  padding: 5px 7px;
  background: rgba(0, 0, 0, 0.28);
  border-radius: 6px;
  text-align: center;
}
.def-box-title { font-size: 9px; font-weight: bold; letter-spacing: 0.4px; text-transform: uppercase; opacity: 0.75; margin-bottom: 4px; }
.def-rows { display: flex; flex-wrap: wrap; gap: 4px; justify-content: center; }
.def-row { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: bold; padding: 2px 8px; border-radius: 10px; text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5); }
.def-pct { font-variant-numeric: tabular-nums; opacity: 0.95; }
.def-running { display: block; font-size: 9px; opacity: 0.55; margin-top: 4px; }
/* Per-team win% pill, anchored to the right of its colored team chip. The
   favorite (>=50%) gets a brighter, ringed pill so it pops at a glance. */
.chip-odds {
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 10px;
  font-weight: bold;
  font-variant-numeric: tabular-nums;
  line-height: 15px;
  padding: 0 5px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.30);
  color: rgba(255, 255, 255, 0.80);
  text-shadow: none;
}
.chip-odds.fav {
  background: rgba(0, 0, 0, 0.52);
  color: #fff;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.45);
}

/* Solid team color chip — fixes legibility of the 30% wash (esp. black/white) */
.team-chip {
  position: relative;
  font-size: 11px;
  font-weight: bold;
  text-align: center;
  padding: 1px 0;
  margin-bottom: 4px;
  border-radius: 3px;
  color: #fff;
  text-shadow: 1px 1px 1px rgb(0 0 0 / 70%);
}
.crown { text-shadow: none; }
.red-team    .team-chip { background: #c0392b; }
.blue-team   .team-chip { background: #2647c8; }
.green-team  .team-chip { background: #2e9e2e; }
.yellow-team .team-chip { background: #b9960f; }
.white-team  .team-chip { background: #d9d9d9; color: #222; text-shadow: none; }
.black-team  .team-chip { background: #222; }
.purple-team .team-chip { background: #6a1b9a; }
.brown-team  .team-chip { background: #6d4326; }
.champion-team .team-chip,
.champ-team    .team-chip { background: #e07b1a; }

/* Unit icon grid: 2x2 in QF cells, 1x4 in the full-width SF/Final/Champ cells */
.unit-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 2px;
}
.semi .unit-grid,
.final .unit-grid,
.champ .unit-grid {
  grid-template-columns: repeat(4, 1fr);
}
.unit-cell {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  padding: 2px 1px;
  border-radius: 4px;
}
.unit-icon {
  width: 100%;
  max-width: 46px;
  height: auto;
  aspect-ratio: 1 / 1;
  object-fit: contain;
}
/* Left team faces right, toward their opponents. */
.left-team .unit-icon { transform: scaleX(-1); }
.unit-name {
  font-size: 11px;
  line-height: 1.1;
  text-align: center;
  word-break: break-word;
  max-width: 100%;
}
.is-boss {
  box-shadow: 0 0 6px 1px gold;
  background: rgba(255, 215, 0, 0.12);
}
.boss-badge { color: gold; margin-right: 1px; }

/* Current (next undecided) match — pulsing glow */
.tournament-bracket__match.is-current {
  animation: current-pulse 1.6s ease-in-out infinite;
  border-radius: 6px;
}
@keyframes current-pulse {
  0%, 100% { box-shadow: 0 0 4px 1px rgba(0, 200, 255, 0.5); }
  50%      { box-shadow: 0 0 14px 4px rgba(0, 200, 255, 0.95); }
}
</style>
